import { Fragment, useCallback } from 'react';
import cn from 'classnames';

import { IKnmPreview } from 'ApiPackage/SearchModule/interfaces/IKnmPreview';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_FULL, DATE_FORMAT_LONG } from 'CorePackage/MainModule/constants/dateFormat';
import { Checkbox } from 'UIPackage/MainModule/components/Checkbox';

import { KnmSubtitle } from './KnmSubtitle';
import styles from './styles.module.scss';
import { parseErknmValue } from '../common/parseErknmValue';
import { ErknmFaceOrganization } from './ErknmFaceOrganization';

interface IErknmFaceProps {
  passportData: IKnmPreview;
}

export const ErknmFace = ({ passportData }: IErknmFaceProps) => {
  const openErvkGovRu = useCallback((cosId: string) => {
    window.open(`https://ervk.gov.ru/objects/${cosId}`, '_blank');
  }, []);

  return (
    <div className={styles.ColumnContent}>
      {passportData?.inspectionErknm?.organizations && passportData?.inspectionErknm?.organizations.length > 0 && (
        <KnmSubtitle title="Сведения о контролируемых лицах">
          {passportData?.inspectionErknm?.organizations.map((item, index) => (
            <ErknmFaceOrganization key={index} organization={item} index={index} />
          ))}
        </KnmSubtitle>
      )}
      {passportData?.inspectionErknm?.organizations && passportData?.inspectionErknm?.organizations[0].okveds && (
        <>
          <span className={styles.SectionTitle}>ОКВЭД</span>
          {passportData?.inspectionErknm?.organizations[0].okveds.map((item, index) => (
            <div key={index}>
              <span className={styles.SectionTitle}>ОКВЭД №{index + 1}</span>
              {item.name && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Наименование</div>
                  <div className={styles.ColValue}>{item.name}</div>
                </div>
              )}
            </div>
          ))}
        </>
      )}
      {passportData?.inspectionErknm?.objects && passportData?.inspectionErknm?.objects.length > 0 && (
        <KnmSubtitle title="Сведения об объектах контроля">
          {passportData?.inspectionErknm?.objects.map((item, index) => (
            <div key={index}>
              <span className={styles.SectionTitle}>Объект №{index + 1}</span>
              {item.cosId && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Номер объекта</div>
                  <div
                    className={cn(styles.ColValue, { [styles.ColValueLink]: !!item.cosId })}
                    onClick={() => item.cosId && openErvkGovRu(item.cosId as string)}
                  >
                    {item.cosId}
                  </div>
                </div>
              )}
              <div className={styles.Row}>
                <div className={styles.ColText}>Иное указание места (нет в ФИАС)</div>
                <div className={styles.ColValue}>
                  <Checkbox checked={item.notInFIAS} size="medium" type="checkbox" theme="system" disabled={true} />
                </div>
              </div>
              {item.address && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Местонахождение</div>
                  <div className={styles.ColValue}>{item.address}</div>
                </div>
              )}
              {item.manualAddress && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Местонахождение</div>
                  <div className={styles.ColValue}>{item.manualAddress}</div>
                </div>
              )}
              {item.region && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Субъект РФ</div>
                  <div className={styles.ColValue}>{item.region}</div>
                </div>
              )}
              {item.objectType && item.objectType.value && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Тип объекта</div>
                  <div className={styles.ColValue}>
                    {parseErknmValue<{ title?: string }>(item.objectType.value)?.title}
                  </div>
                </div>
              )}
              {item.objectKind && item.objectKind.value && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Вид объекта</div>
                  <div className={styles.ColValue}>
                    {parseErknmValue<{ title?: string }>(item.objectKind.value)?.title}
                  </div>
                </div>
              )}
              {item.objectSubKind && item.objectSubKind.value && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Подвид объекта</div>
                  <div className={styles.ColValue}>
                    {parseErknmValue<{ title?: string }>(item.objectSubKind.value)?.title}
                  </div>
                </div>
              )}
              {item.riskCategory && item.riskCategory.value && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Категория риска</div>
                  <div className={styles.ColValue}>
                    {parseErknmValue<{ title?: string }>(item.riskCategory.value)?.title}
                  </div>
                </div>
              )}
              {item.dangerClass && item.dangerClass.value && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Класс опасности</div>
                  <div className={styles.ColValue}>
                    {parseErknmValue<{ title?: string }>(item.dangerClass.value)?.title}
                  </div>
                </div>
              )}
            </div>
          ))}
        </KnmSubtitle>
      )}
      {passportData?.inspectionErknm?.documentRequestDate && (
        <KnmSubtitle title="Дата направления требования представить необходимые для рассмотрения в ходе документарной проверки документы">
          <div className={styles.Row}>
            <div className={styles.ColText}>Дата</div>
            <div className={styles.ColValue}>
              {formatDate({ date: passportData?.inspectionErknm?.documentRequestDate, dateFormat: DATE_FORMAT_LONG })}
            </div>
          </div>
        </KnmSubtitle>
      )}
      {passportData?.inspectionErknm?.documentResponseDate && (
        <KnmSubtitle
          title="Дата получения документов во исполнение требования представить необходимые для рассмотрения в ходе
      документарной проверки документы"
        >
          <div className={styles.Row}>
            <div className={styles.ColText}>Дата</div>
            <div className={styles.ColValue}>
              {formatDate({ date: passportData?.inspectionErknm?.documentResponseDate, dateFormat: DATE_FORMAT_LONG })}
            </div>
          </div>
        </KnmSubtitle>
      )}
      {passportData?.inspectionErknm?.experts && passportData?.inspectionErknm?.experts.length > 0 && (
        <KnmSubtitle
          title="Сведения об экспертах, экспертных организациях, специалистах,
      независимых органах инспекции, саморегулируемых организациях и иных лицах, привлекаемых для проведения КНМ"
        >
          {passportData?.inspectionErknm?.experts.map((item, index) => (
            <div key={index}>
              <span className={styles.SectionTitle}>Должностное лицо №{index + 1}</span>
              {item.title && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Наименование</div>
                  <div className={styles.ColValue}>{item.title}</div>
                </div>
              )}
              {item.type && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Тип</div>
                  <div className={styles.ColValue}>{item.type.name}</div>
                </div>
              )}
            </div>
          ))}
        </KnmSubtitle>
      )}
      <KnmSubtitle title="Проведение мероприятия в форме совместного (межведомственного) контрольного (надзорного) мероприятия">
        <div className={styles.Row}>
          <div className={styles.ColText}>Проводится совместно</div>
          <div className={styles.ColValue}>
            {passportData?.inspectionErknm?.isHasCollaboratingOrganization ? 'Да' : 'Нет'}
          </div>
        </div>
        {passportData?.inspectionErknm?.collaboratingOrganizations &&
          passportData?.inspectionErknm?.collaboratingOrganizations.length > 0 && (
            <>
              <span className={styles.SectionTitle}>
                Наименование органов контроля , с которым КНМ проводится совместно
              </span>
              {passportData?.inspectionErknm?.collaboratingOrganizations.map((item, index) => (
                <div key={index}>
                  <span className={styles.SectionTitle}>Орган контроля №{index + 1}</span>
                  {item.organization && item.organization.value && (
                    <div className={styles.Row}>
                      <div className={styles.ColText}>Наименование</div>
                      <div className={styles.ColValue}>
                        {parseErknmValue<{ fullName?: string }>(item.organization.value)?.fullName}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
      </KnmSubtitle>
      {(passportData?.inspectionErknm?.interactionCategory || passportData?.inspectionErknm?.isRemote !== null) && (
        <KnmSubtitle title="Сведения об использовании в рамках КНМ средств дистанционного взаимодействия с контролируемыми лицами">
          {passportData?.inspectionErknm?.interactionCategory && (
            <div className={styles.Row}>
              <div className={styles.ColText}>Категория взаимодействия</div>
              <div className={styles.ColValue}>{passportData?.inspectionErknm?.interactionCategory?.name}</div>
            </div>
          )}
          {!passportData?.inspectionErknm?.interactionCategory && (
            <div className={styles.Row}>
              <div className={styles.ColText}>Отметка об использовании</div>
              <div className={styles.ColValue}>{passportData?.inspectionErknm?.isRemote ? 'Да' : 'Нет'}</div>
            </div>
          )}
          {!passportData?.inspectionErknm?.interactionCategory && passportData?.inspectionErknm?.noteRemote && (
            <div className={styles.Row}>
              <div className={styles.ColText}>Сведения</div>
              <div className={styles.ColValue}>{passportData?.inspectionErknm?.noteRemote}</div>
            </div>
          )}
        </KnmSubtitle>
      )}
      {passportData?.inspectionErknm?.events && passportData?.inspectionErknm?.events.length > 0 && (
        <KnmSubtitle title="Перечень действий, осуществляемых в рамках КНМ">
          {passportData?.inspectionErknm?.events.map((item, index) => (
            <div key={index}>
              <span className={styles.SectionTitle}>Действие №{index + 1}</span>
              {item.event && item.event.value && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Действие</div>
                  <div className={styles.ColValue}>{parseErknmValue<{ title?: string }>(item.event.value)?.title}</div>
                </div>
              )}
              {item.interactionCategory && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Категория взаимодействия</div>
                  <div className={styles.ColValue}>{item.interactionCategory.name}</div>
                </div>
              )}
              {item.startDate && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Дата начала</div>
                  <div className={styles.ColValue}>
                    {formatDate({ date: item.startDate, dateFormat: DATE_FORMAT_LONG })}
                  </div>
                </div>
              )}
              {item.stopDate && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Дата окончания</div>
                  <div className={styles.ColValue}>
                    {formatDate({ date: item.stopDate, dateFormat: DATE_FORMAT_LONG })}
                  </div>
                </div>
              )}
              {item.submittedDocuments &&
                item.submittedDocuments.map((submittedDocument, sdIndex) => (
                  <Fragment key={sdIndex}>
                    <div className={styles.Row}>
                      <div className={styles.ColText}>Наименование документа №{sdIndex + 1}</div>
                      <div className={styles.ColValue}>{submittedDocument.documentTitle}</div>
                    </div>
                    <div className={styles.Row}>
                      <div className={styles.ColText}>Дата и время запроса документа №{sdIndex + 1}</div>
                      <div className={styles.ColValue}>
                        {formatDate({ date: submittedDocument.requestDateTime, dateFormat: DATE_FORMAT_FULL })}
                      </div>
                    </div>
                    <div className={styles.Row}>
                      <div className={styles.ColText}>Описание документа №{sdIndex + 1}</div>
                      <div className={styles.ColValue}>{submittedDocument.description || ''}</div>
                    </div>
                    <div className={styles.Row}>
                      <div className={styles.ColText}>Срок представления документа №{sdIndex + 1}</div>
                      <div className={styles.ColValue}>
                        {formatDate({ date: submittedDocument.submissionDeadline, dateFormat: DATE_FORMAT_FULL })}
                      </div>
                    </div>
                    <div className={styles.Row}>
                      <div className={styles.ColText}>Статус представления документа №{sdIndex + 1}</div>
                      <div className={styles.ColValue}>{submittedDocument.status.title}</div>
                    </div>
                    <div className={styles.Row}>
                      <div className={styles.ColText}>Дата и время получения документа №{sdIndex + 1}</div>
                      <div className={styles.ColValue}>
                        {formatDate({ date: submittedDocument.submissionDateTime || '', dateFormat: DATE_FORMAT_FULL })}
                      </div>
                    </div>
                  </Fragment>
                ))}
            </div>
          ))}
        </KnmSubtitle>
      )}
    </div>
  );
};
